'use client';

import { useState, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

interface Value {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
}

export default function ValuesSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeValue, setActiveValue] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const values: Value[] = [{
    id: 1,
    title: 'Integrity',
    description: 'Honest communication, transparent processes, and unwavering commitment to delivering what we promise. Trust is earned through consistent action.',
    image: "/assets/images/Desk_Image-1772626809490.jpg",
    alt: 'Office desk workspace'
  },
  {
    id: 2,
    title: 'Excellence',
    description: 'Relentless pursuit of perfection in every detail. Mediocrity is not an option when your reputation depends on the quality of your work.',
    image: "/assets/images/Desk_Image-1772626809490.jpg",
    alt: 'Office desk workspace'
  },
  {
    id: 3,
    title: 'Innovation',
    description: 'Pushing boundaries while respecting timeless principles. Progress requires courage to challenge conventions and explore uncharted territories.',
    image: "/assets/images/Desk_Image-1772626809490.jpg",
    alt: 'Office desk workspace'
  },
  {
    id: 4,
    title: 'Collaboration',
    description: 'Great work emerges from genuine partnership. Your insights combined with my expertise create solutions neither could achieve alone.',
    image: "/assets/images/Desk_Image-1772626809490.jpg",
    alt: 'Office desk workspace'
  }];


  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full px-6 lg:px-12 py-32 lg:py-48 bg-secondary">

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div
          className={`max-w-3xl mb-24 transition-all duration-slow ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
          }>

          <p className="text-sm font-cta font-semibold tracking-[0.2em] text-muted-foreground uppercase mb-6">
            Values
          </p>
          <h2 className="text-4xl lg:text-6xl font-headline font-bold leading-[1.1] text-primary mb-8">
            The Foundation of Every Partnership
          </h2>
          <p className="text-xl font-body text-text-secondary leading-relaxed">These core values shape how we work, communicate, and deliver results for every client relationship.

          </p>
        </div>

        {/* Values Grid */}
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Values List */}
          <div className="space-y-8">
            {values.map((value, index) =>
            <div
              key={value.id}
              className={`group cursor-pointer transition-all duration-slow ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`
              }
              style={{ transitionDelay: `${index * 100}ms` }}
              onMouseEnter={() => setActiveValue(index)}>

                <div
                className={`p-8 border-l-4 transition-all duration-fast ${
                activeValue === index ?
                'border-primary bg-muted' : 'border-border bg-transparent hover:bg-muted/50'}`
                }>

                  <h3 className="text-2xl lg:text-3xl font-headline font-bold text-primary mb-4">
                    {value.title}
                  </h3>
                  <p className="text-lg font-body text-text-secondary leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Active Value Image */}
          <div
            className={`relative transition-all duration-slow delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`
            }>

            <div className="sticky top-32">
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
                <AppImage
                  src={values[activeValue].image}
                  alt={values[activeValue].alt}
                  priority={true}
                  className="w-full h-full object-cover transition-all duration-medium" />

                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
                
                {/* Value Title Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-primary to-transparent">
                  <p className="text-3xl font-headline font-bold text-primary-foreground">
                    {values[activeValue].title}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}